export function getNextFriday(dateString: string): string {
  // Parse the date string (YYYY-MM-DD) as a local date to avoid timezone shifts
  const [year, month, day] = dateString.split('-').map(Number)
  const date = new Date(year, month - 1, day)

  const dayOfWeek = date.getDay()
  // Calculate days until next Friday (Friday is 5)
  // If today is Friday (5), we want the *next* Friday (+7 days)
  // Sun-Sat calendar week pays the NEXT Friday (after that Saturday)
  // Saturday is soonest (6 days), Sunday is longest (12 days)
  const daysToAdd = 12 - dayOfWeek

  date.setDate(date.getDate() + daysToAdd)

  // Format back to YYYY-MM-DD
  const nextYear = date.getFullYear()
  const nextMonth = String(date.getMonth() + 1).padStart(2, '0')
  const nextDay = String(date.getDate()).padStart(2, '0')

  return `${nextYear}-${nextMonth}-${nextDay}`
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)
}

export function formatPercent(decimal: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(decimal)
}

export const SIT_PAY_AMOUNT = 150

// Commission caps by project type
const COMMISSION_RULES: Record<
  string,
  {
    closerCap: number | null
    setterCap: number | null | ((projectTotal: number) => number)
    closerPaid: boolean
    setterPaid: boolean
  }
> = {
  Windows: {
    closerCap: null, // No cap
    setterCap: null, // No cap
    closerPaid: true,
    setterPaid: true,
  },
  Roofs: {
    closerCap: 1000, // Closer capped at $1000
    setterCap: 500, // Setter capped at $500
    closerPaid: true,
    setterPaid: true,
  },
  'General Contracting': {
    closerCap: null,
    setterCap: (projectTotal: number) => (projectTotal >= 20000 ? 1000 : 500),
    closerPaid: false, // Closer does NOT get commission
    setterPaid: true,
  },
  Hardscape: {
    closerCap: null,
    setterCap: (projectTotal: number) => (projectTotal >= 20000 ? 1000 : 500),
    closerPaid: false, // Closer does NOT get commission (same as GC)
    setterPaid: true,
  },
}

// Default rules for unknown project types (backward compat)
const DEFAULT_RULES = {
  closerCap: null,
  setterCap: null,
  closerPaid: true,
  setterPaid: true,
}

function getSetterCap(
  rules: typeof DEFAULT_RULES & { setterCap: any },
  projectTotal: number,
): number | null {
  if (typeof rules.setterCap === 'function') {
    return rules.setterCap(projectTotal)
  }
  return rules.setterCap
}

// Only Windows pays sit pay
const SIT_PAY_PROJECT_TYPES = ['Windows']

export function calculateJobDetails(
  isClosed: boolean,
  projectType: string,
  projectTotal: number,
  financeCompany: string,
  financeFee: number,
  depositPaid: boolean,
  closerRate: number,
  setterRate: number,
  hasSetter: boolean,
) {
  const qualifiesForSitPay = SIT_PAY_PROJECT_TYPES.includes(projectType)
  const sitPay = hasSetter && qualifiesForSitPay ? SIT_PAY_AMOUNT : 0

  if (!isClosed) {
    return {
      isFinanced: false,
      netAmount: 0,
      closerCommission: 0,
      setterCommission: 0,
      sitPay,
      status: sitPay > 0 ? ('Sit Only' as const) : ('Sit Only' as const),
    }
  }

  const rules = COMMISSION_RULES[projectType] || DEFAULT_RULES

  const isFinanced = financeCompany !== 'None' && financeCompany.trim() !== ''
  const netAmount = isFinanced
    ? Math.max(0, projectTotal - financeFee)
    : projectTotal

  // Closer commission
  let closerCommission = 0
  if (rules.closerPaid) {
    closerCommission = netAmount * closerRate
    if (rules.closerCap !== null) {
      closerCommission = Math.min(closerCommission, rules.closerCap)
    }
  }

  // Setter commission
  let setterCommission = 0
  if (rules.setterPaid && hasSetter) {
    setterCommission = netAmount * setterRate
    const setterCap = getSetterCap(rules as any, projectTotal)
    if (setterCap !== null) {
      setterCommission = Math.min(setterCommission, setterCap)
    }
  }

  const status = isFinanced || depositPaid ? 'Ready' : 'Pending Deposit'

  return {
    isFinanced,
    netAmount,
    closerCommission,
    setterCommission,
    sitPay,
    status,
  }
}
